/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { LearningExperience } from '@app/app/page-modules/learning-experiences/learning-experience.model';
import { PortfolioUpdateRequestModel } from '@app/app/page-modules/portfolio/models';
import { Page } from '@app/app/shared/models/page';
import { DynamicContentRequest } from '@app/app/shared/models/page/page.model';
import { PortfolioDataService } from '@app/app/shared/services/portfolio/data.service';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs';
import { RestClientService } from 'src/app/shared/services/rest-client.service';
import { ObservableResult } from 'src/app/shared/store';
import { environment } from 'src/environments/environment';

export interface FolioItemState {
  folioUid: string;
  isVisible: boolean;
}

export interface FolioSection {
  uid: string;
  name: string;
  content: DynamicContentRequest[];
  learningExperienceIds: string[];
  reflections: Record<string, string>;
}

export interface PortfolioModel {
  uid: string;
  title: string;
  description: string | null;
  folioItemState: FolioItemState;
  learningExperiences?: LearningExperience[];
  sections: FolioSection[];
  userOrganizationName: string;
  shareUrl: string | null;
  cohortShared: boolean;
  updatedAt: string;
}

export class ApiPortfolioDataService implements PortfolioDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getPortfolio(portfolioId: string): ObservableResult<PortfolioModel> {
    return this.client.get<PortfolioModel>(Location.joinWithSlash(environment.apiRootUrl || '', `portfolios/${portfolioId}`), null).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetPortfolio'))),
    );
  }

  getUserPortfolios(page: number, pageSize: number): ObservableResult<Page<PortfolioModel>> {
    return this.client
      .get<Page<PortfolioModel>>(Location.joinWithSlash(environment.apiRootUrl || '', `portfolios/search`), {
        page: page.toString(),
        pageSize: pageSize.toString(),
      })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorSearchPortfolios'))),
      );
  }

  getPublicPortfolio(portfolioId: string): ObservableResult<PortfolioModel> {
    return this.client
      .get<PortfolioModel>(Location.joinWithSlash(environment.apiRootUrl || '', `portfolios/public/${portfolioId}`), null)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetPortfolio'))),
      );
  }

  updatePortfolio(portfolioId: string, request: PortfolioUpdateRequestModel): ObservableResult<PortfolioModel> {
    return this.client.put<PortfolioModel>(Location.joinWithSlash(environment.apiRootUrl || '', `portfolios/${portfolioId}`), request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioStatus'))),
    );
  }

  createPortfolio(request: PortfolioUpdateRequestModel): ObservableResult<PortfolioModel> {
    return this.client.post<PortfolioModel>(Location.joinWithSlash(environment.apiRootUrl || '', `portfolios/create`), request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioStatus'))),
    );
  }

  updatePortfolioVisibility(portfolioId: string, isVisible: boolean): ObservableResult<PortfolioModel> {
    const params = new HttpParams().set('isVisible', isVisible);

    return this.client
      .put<PortfolioModel>(Location.joinWithSlash(environment.apiRootUrl || '', `portfolios/${portfolioId}/visibility`), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioStatus'))),
      );
  }

  deletePortfolio(portfolioId: string): ObservableResult<void> {
    return this.client.delete<void>(Location.joinWithSlash(environment.apiRootUrl || '', `portfolios/${portfolioId}`)).pipe(
      switchMap(() => ObservableResult.ofSuccess(null)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioStatus'))),
    );
  }

  updatePortfolioCohortShare(portfolioId: string, shared: boolean): ObservableResult<PortfolioModel> {
    return this.client
      .put<PortfolioModel>(Location.joinWithSlash(environment.apiRootUrl || '', `portfolios/${portfolioId}/cohort-share`), { shared })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioStatus'))),
      );
  }
}
